<template>
  <aside class="ai-sidebar" :class="{ 'ai-sidebar--collapsed': collapsed }" data-tutorial="history">
    <div class="ai-sidebar__top">
      <button
        type="button"
        class="ai-sidebar__new-chat"
        :class="{ 'ai-sidebar__new-chat--collapsed': collapsed }"
        :aria-label="collapsed ? 'New chat' : undefined"
        :title="collapsed ? 'New chat' : undefined"
        @click="$emit('new-chat')"
      >
        <v-icon small :left="!collapsed">mdi-plus</v-icon>
        <span v-if="!collapsed">New chat</span>
      </button>
    </div>

    <div v-show="!collapsed" class="ai-sidebar__search">
      <v-text-field
        :value="threadSearchInput"
        placeholder="Search chats"
        dense
        solo
        flat
        hide-details
        prepend-inner-icon="mdi-magnify"
        clearable
        background-color="#ececf1"
        class="ai-sidebar__search-field"
        @input="$emit('update:threadSearchInput', $event)"
      />
    </div>

    <div v-show="!collapsed" ref="threadListViewport" class="ai-sidebar__list">
      <!-- Background refresh over an existing list -->
      <v-overlay absolute :value="isThreadsLoading && threads.length > 0">
        <v-progress-circular indeterminate size="32" color="primary" />
      </v-overlay>
      <!-- Initial load: skeleton rows -->
      <div v-if="isThreadsLoading && !threads.length" class="ai-sidebar__skeletons">
        <v-skeleton-loader
          v-for="n in 8"
          :key="n"
          type="list-item"
          class="ai-sidebar__skeleton"
        />
      </div>
      <!-- Plain scrollable list: a v-tooltip per row does not survive
           v-virtual-scroll's node recycling (leaves blank grey slots), and chat
           thread counts are modest, so virtualisation isn't needed here. -->
      <div v-else class="ai-sidebar__threads">
        <div v-for="item in threads" :key="item.id" class="ai-sidebar__thread-slot">
          <v-tooltip right>
            <template v-slot:activator="{ on, attrs }">
              <button
                type="button"
                class="ai-sidebar__thread"
                v-bind="attrs"
                :class="{
                  'ai-sidebar__thread--active': activeThreadId === item.id,
                  'ai-sidebar__thread--busy': isThreadBusy(item.id),
                }"
                @click="$emit('select-thread', item)"
                v-on="on"
              >
                <span class="ai-sidebar__thread-icon-wrap" aria-hidden="true">
                  <v-icon size="18" class="ai-sidebar__thread-icon">mdi-message-text-outline</v-icon>
                </span>
                <span class="ai-sidebar__thread-label">{{ item.title || 'Untitled' }}</span>
                <v-btn
                  icon
                  x-small
                  class="ai-sidebar__thread-delete"
                  aria-label="Delete chat"
                  @click.stop="$emit('delete-thread', item)"
                >
                  <v-icon small>mdi-delete-outline</v-icon>
                </v-btn>
              </button>
            </template>
            <span>{{ item.title || 'Untitled' }}</span>
          </v-tooltip>
        </div>
      </div>
    </div>

    <tracey-ai-agent-info :collapsed="collapsed" />
  </aside>
</template>

<script>
import TraceyAiAgentInfo from './TraceyAiAgentInfo.vue';

export default {
  name: 'TraceyAiSidebar',
  components: {
    TraceyAiAgentInfo,
  },
  props: {
    threads: { type: Array, default: () => [] },
    activeThreadId: { type: String, default: null },
    threadSearchInput: { type: String, default: '' },
    isThreadsLoading: { type: Boolean, default: false },
    threadListHeight: { type: Number, default: 400 },
    collapsed: { type: Boolean, default: false },
    isThreadBusy: { type: Function, required: true },
  },
  data() {
    return {
      threadItemHeight: 52,
    };
  },
};
</script>

<style scoped>
.ai-sidebar {
  position: relative;
  display: flex;
  flex-direction: column;
  width: 260px;
  min-width: 260px;
  height: 100%;
  background: #f7f7f8;
  border-right: 1px solid #ececf1;
  transition: width 0.2s ease, min-width 0.2s ease;
}

.ai-sidebar--collapsed {
  width: 52px;
  min-width: 52px;
}

.ai-sidebar__top {
  padding: 10px 10px 0;
  margin-bottom: 10px;
  flex-shrink: 0;
}

.ai-sidebar__new-chat {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  width: 100%;
  min-height: 40px;
  padding: 0 12px;
  border: 1px solid #d9d9e3;
  border-radius: 8px;
  background: #fff;
  color: #0d0d0d;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.15s;
}

.ai-sidebar__new-chat:hover {
  background: #ececf1;
}

.ai-sidebar--collapsed .ai-sidebar__top {
  display: flex;
  justify-content: center;
  padding: 10px 8px 0;
  margin-bottom: 8px;
}

.ai-sidebar__new-chat--collapsed {
  width: 36px;
  min-width: 36px;
  height: 36px;
  padding: 0;
  justify-content: center;
}

.ai-sidebar__new-chat--collapsed ::v-deep .v-icon {
  margin: 0 !important;
}

.ai-sidebar__search {
  padding: 0 10px 8px;
  flex-shrink: 0;
}

.ai-sidebar__search-field ::v-deep .v-input__slot {
  border-radius: 8px !important;
  min-height: 36px !important;
}

.ai-sidebar__list {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  position: relative;
  padding: 6px 0 8px;
}

.ai-sidebar__skeletons {
  padding: 4px 8px;
}

.ai-sidebar__skeleton {
  background: transparent;
}

.ai-sidebar__thread-slot {
  height: 52px;
  padding: 0 10px 4px;
  box-sizing: border-box;
}

.ai-sidebar__thread {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  height: 48px;
  padding: 0 10px 0 8px;
  border: 1px solid transparent;
  border-radius: 10px;
  background: transparent;
  text-align: left;
  cursor: pointer;
  color: #4a4a5a;
  font-size: 13px;
  line-height: 1.3;
  transition: background 0.15s, border-color 0.15s, color 0.15s, box-shadow 0.15s;
}

.ai-sidebar__thread:hover {
  background: #ececf1;
  color: #0d0d0d;
}

.ai-sidebar__thread--active {
  background: #fff;
  border-color: #c5cdd8;
  color: #0d0d0d;
  font-weight: 600;
  box-shadow: 0 1px 3px rgba(18, 37, 58, 0.12);
}

.ai-sidebar__thread--active .ai-sidebar__thread-icon-wrap {
  background: #12253a;
}

.ai-sidebar__thread--active .ai-sidebar__thread-icon {
  color: #fff !important;
}

.ai-sidebar__thread--busy:not(.ai-sidebar__thread--active) {
  animation: ai-sidebar-busy 1.5s ease-in-out infinite;
}

.ai-sidebar__thread--busy.ai-sidebar__thread--active {
  animation: ai-sidebar-busy-active 1.5s ease-in-out infinite;
}

@keyframes ai-sidebar-busy {
  0%, 100% { background: transparent; }
  50% { background: rgba(255, 20, 99, 0.1); }
}

@keyframes ai-sidebar-busy-active {
  0%, 100% { box-shadow: 0 1px 3px rgba(18, 37, 58, 0.12); }
  50% { box-shadow: 0 0 0 2px rgba(255, 20, 99, 0.35); }
}

.ai-sidebar__thread-icon-wrap {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 30px;
  height: 30px;
  border-radius: 8px;
  background: #e8e8ed;
  transition: background 0.15s;
}

.ai-sidebar__thread-icon {
  color: #5c5c6f !important;
  margin: 0 !important;
}

.ai-sidebar__thread-label {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  line-height: 1.35;
  padding-top: 1px;
}

.ai-sidebar__thread-delete {
  flex-shrink: 0;
  margin: 0 -4px 0 0;
  opacity: 0;
  transition: opacity 0.15s;
}

.ai-sidebar__thread-delete ::v-deep .v-btn {
  width: 28px !important;
  height: 28px !important;
}

.ai-sidebar__thread:hover .ai-sidebar__thread-delete,
.ai-sidebar__thread--active .ai-sidebar__thread-delete,
.ai-sidebar__thread:focus-within .ai-sidebar__thread-delete {
  opacity: 1;
}

.ai-sidebar__bottom {
  flex-shrink: 0;
  margin-top: auto;
}

.ai-sidebar__footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 14px;
  border-top: 1px solid #ececf1;
  flex-shrink: 0;
}

.ai-sidebar__beta {
  font-size: 11px;
  font-weight: 600;
  color: #12253a;
  background: #e6f0f5;
  padding: 2px 8px;
  border-radius: 4px;
}

.ai-sidebar__feedback {
  border: none;
  background: none;
  font-size: 12px;
  color: #6e6e80;
  cursor: pointer;
  padding: 0;
}

.ai-sidebar__feedback:hover {
  color: #ff1463;
}

.ai-sidebar__footer--collapsed {
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 10px 8px;
}

.ai-sidebar__footer--collapsed .ai-sidebar__beta {
  font-size: 9px;
  padding: 2px 6px;
  line-height: 1.2;
}

.ai-sidebar__feedback--collapsed {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border: 1px solid #d9d9e3;
  border-radius: 8px;
  background: #fff;
}

.ai-sidebar__feedback--collapsed:hover {
  background: #ececf1;
  color: #ff1463;
}

@media (max-width: 768px) {
  .ai-sidebar {
    position: absolute;
    z-index: 30;
    left: 0;
    top: 0;
    bottom: 0;
    box-shadow: 4px 0 24px rgba(0, 0, 0, 0.12);
  }

  .ai-sidebar--collapsed {
    transform: translateX(-100%);
    width: 260px;
    min-width: 260px;
    pointer-events: none;
  }
}
</style>
